import { getAdminAuthHeaders } from './admin-api';

const API_BASE = '/api/admin/prompts';

export interface PromptVersion {
  id: number;
  clinicalSetting: string;
  body: string;
  createdBy: string;
  createdAt: string;
  isActive: boolean;
  dataScope: string[] | null;
}

export interface PromptListEntry {
  id: string;
  displayName: string;
  activeVersion: { id: number; createdAt: string; createdBy: string } | null;
}

export interface PromptDetail {
  setting: string;
  body: string;
  source: 'db' | 'markdown';
  version: { id: number; createdAt: string; createdBy: string } | null;
  dataScope: string[];
  dataScopeSource: 'db' | 'default';
}

async function jsonFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    headers: { 'Content-Type': 'application/json', ...getAdminAuthHeaders(), ...(init?.headers ?? {}) },
    ...init,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`${res.status}: ${text}`);
  }
  return res.json() as Promise<T>;
}

export async function listPrompts(): Promise<{ settings: PromptListEntry[] }> {
  return jsonFetch(`${API_BASE}`);
}

export async function getPrompt(setting: string): Promise<PromptDetail> {
  return jsonFetch(`${API_BASE}/${setting}`);
}

export async function getPromptHistory(setting: string): Promise<{ versions: PromptVersion[] }> {
  return jsonFetch(`${API_BASE}/${setting}/history`);
}

export async function savePrompt(
  setting: string,
  body: string,
  dataScope: string[] | null,
): Promise<{ version: PromptVersion }> {
  return jsonFetch(`${API_BASE}/${setting}`, {
    method: 'POST',
    body: JSON.stringify({ body, dataScope }),
  });
}

export async function revertPrompt(setting: string, versionId: number): Promise<{ version: PromptVersion }> {
  return jsonFetch(`${API_BASE}/${setting}/revert/${versionId}`, { method: 'POST' });
}

export interface ScopeSectionMeta {
  id: string;
  label: string;
  description: string;
}

export async function listScopeSections(): Promise<{ sections: ScopeSectionMeta[] }> {
  return jsonFetch(`${API_BASE}/scope-sections`);
}

export interface CustomSpecialty {
  id: number;
  slug: string;
  displayName: string;
  group: 'validated' | 'pediatric' | 'adult';
  createdBy: string;
  createdAt: string;
}

export async function listCustomSpecialties(): Promise<{ specialties: CustomSpecialty[] }> {
  return jsonFetch('/api/admin/specialties');
}

export async function createSpecialty(args: {
  displayName: string;
  slug: string;
  group: 'validated' | 'pediatric' | 'adult';
  dataScope: string[];
  body: string;
}): Promise<{ specialty: CustomSpecialty; version: PromptVersion }> {
  return jsonFetch('/api/admin/specialties', {
    method: 'POST',
    body: JSON.stringify(args),
  });
}

/**
 * Streams chunks from POST /:setting/test as SSE.
 */
export async function streamPromptTest(
  setting: string,
  args: { message: string; draftBody?: string },
  onChunk: (chunk: string) => void,
): Promise<void> {
  const res = await fetch(`${API_BASE}/${setting}/test`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAdminAuthHeaders() },
    body: JSON.stringify(args),
  });
  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => '');
    throw new Error(`${res.status}: ${text}`);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const events = buffer.split('\n\n');
    buffer = events.pop() ?? '';
    for (const evt of events) {
      const lines = evt.split('\n');
      const eventLine = lines.find(l => l.startsWith('event:'));
      const dataLine = lines.find(l => l.startsWith('data:'));
      if (!eventLine || !dataLine) continue;
      const eventType = eventLine.slice(6).trim();
      const data = JSON.parse(dataLine.slice(5).trim());
      if (eventType === 'chunk') onChunk(data.chunk);
      if (eventType === 'error') throw new Error(data.error);
      if (eventType === 'done') return;
    }
  }
}
